import { Trait } from "utils/types"
import { featuresRecordToEntries, traitsToFeatureEntries } from "utils/featureFilters"
import { isArtBlocksChainId } from "utils/artBlocksUrls"

export interface NftTokenMetadata {
  aspect_ratio?: number | string
  aspectRatio?: number | string
  animation_url?: string
  contract_address?: string
  description?: string
  features?: Record<string, unknown>
  generator_url?: string
  image?: string
  is_static?: boolean
  license?: string
  name?: string
  primary_asset_url?: string
  project_id?: string
  token_hash?: string
  tokenID?: string
  traits?: Trait[]
}

const parseRatioPart = (value: string) => {
  const parsed = Number.parseFloat(value.trim())
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

export const parseMetadataAspectRatio = (value?: number | string | null) => {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : undefined
  }

  if (typeof value !== "string") return undefined

  if (value.includes("/") || value.includes(":")) {
    const separator = value.includes("/") ? "/" : ":"
    const [width, height] = value.split(separator)
    const parsedWidth = parseRatioPart(width)
    const parsedHeight = parseRatioPart(height)
    if (parsedWidth && parsedHeight) return parsedWidth / parsedHeight
  }

  return parseRatioPart(value)
}

export const getNftMetadataAspectRatio = (metadata?: NftTokenMetadata | null) =>
  parseMetadataAspectRatio(metadata?.aspect_ratio ?? metadata?.aspectRatio)

export const getNftMetadataGeneratorUrl = (metadata?: NftTokenMetadata | null) =>
  metadata?.generator_url || metadata?.animation_url

export const getNftMetadataImageUrl = (metadata?: NftTokenMetadata | null) =>
  metadata?.image || metadata?.primary_asset_url

export const canRenderNftMetadataLive = (metadata?: NftTokenMetadata | null) =>
  metadata?.is_static !== true && Boolean(getNftMetadataGeneratorUrl(metadata))

// Whether a live (generator) view can be shown for a token. This intentionally
// does NOT require the third-party token metadata to load successfully or to
// contain a generator URL: as long as the token is not explicitly static and we
// have a supported chain id, the generator URL can be constructed from the
// chain id + contract + token id. This keeps the live view working even when the
// token.artblocks.io metadata request fails or omits the generator field.
export const canRenderTokenLive = (
  metadata: NftTokenMetadata | null | undefined,
  chainId?: number | string | null,
) =>
  metadata?.is_static !== true &&
  (Boolean(getNftMetadataGeneratorUrl(metadata)) || isArtBlocksChainId(chainId))

export const getNftMetadataFeatures = (metadata?: NftTokenMetadata | null) => {
  if (metadata?.features && Object.keys(metadata.features).length > 0) {
    return featuresRecordToEntries(metadata.features)
  }

  return traitsToFeatureEntries(metadata?.traits)
}
